/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MOBILE_TABLET_MIGRATION_PHASE1
 * Date:         2026-02-09
 * Modified By:  Claude
 *
 * Description:
 * Created reusable useMediaQuery hook for responsive component rendering.
 * Based on existing pattern from UsersTable.tsx:82-89
 *
 * File Size Check:
 * - Current: NEW FILE
 * - Projected: ~80 lines
 * - Decision: Proceed (within 100 line hook limit)
 *
 * Related Screens / Modules:
 * - client/src/hooks/useMediaQuery.ts (NEW)
 * - client/src/features/admin/users/UsersTable.tsx (pattern source)
 */

import { useState, useEffect, useCallback } from "react";

// Breakpoint values matching _breakpoints.scss
const BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
} as const;

type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";

interface MediaQueryResult {
  isMobile: boolean;      // < 576px
  isTablet: boolean;      // 576px - 991px
  isDesktop: boolean;     // >= 992px
  breakpoint: Breakpoint; // Current breakpoint name
  width: number;          // Current viewport width
}

/**
 * Get current breakpoint based on window width
 */
const getBreakpoint = (width: number): Breakpoint => {
  if (width < BREAKPOINTS.sm) return "xs";
  if (width < BREAKPOINTS.md) return "sm";
  if (width < BREAKPOINTS.lg) return "md";
  if (width < BREAKPOINTS.xl) return "lg";
  if (width < BREAKPOINTS.xxl) return "xl";
  return "xxl";
};

/**
 * Hook for responsive design breakpoint detection
 * @returns Object with isMobile, isTablet, isDesktop, breakpoint, and width
 *
 * @example
 * const { isMobile, isTablet, isDesktop } = useMediaQuery();
 *
 * if (isMobile) {
 *   return <MobileView />;
 * } else if (isTablet) {
 *   return <TabletView />;
 * }
 * return <DesktopView />;
 */
export function useMediaQuery(): MediaQueryResult {
  const [width, setWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : BREAKPOINTS.lg
  );

  const handleResize = useCallback(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    // Set initial width
    handleResize();

    // Add resize listener with debounce for performance
    let timeoutId: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener("resize", debouncedResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedResize);
    };
  }, [handleResize]);

  const breakpoint = getBreakpoint(width);
  const isMobile = width < BREAKPOINTS.sm;
  const isTablet = width >= BREAKPOINTS.sm && width < BREAKPOINTS.lg;
  const isDesktop = width >= BREAKPOINTS.lg;

  return {
    isMobile,
    isTablet,
    isDesktop,
    breakpoint,
    width,
  };
}

/**
 * Hook for custom media query matching
 * @param query - CSS media query string (e.g., "(min-width: 768px)")
 * @returns boolean indicating if the media query matches
 *
 * @example
 * const isLargeScreen = useCustomMediaQuery("(min-width: 1200px)");
 */
export function useCustomMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    }

    // Legacy browsers
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, [query]);

  return matches;
}

export default useMediaQuery;
